import {
  DIFFICULTIES,
  STRATEGIES,
  type BotDifficulty,
  type BotSpec,
  type BotStrategy,
} from '@halvard/ai';
import type { Team } from '@halvard/rules';
import type { Players } from './store';

/** `hard:hunter` ↔ `{ difficulty: 'hard', strategy: 'hunter' }`. */
export function parseSpec(text: string | null): BotSpec | null {
  if (!text) return null;
  const [difficulty, strategy] = text.split(':');
  if (!difficulty || !strategy || !(difficulty in DIFFICULTIES) || !(strategy in STRATEGIES)) {
    return null;
  }
  return { difficulty: difficulty as BotDifficulty, strategy: strategy as BotStrategy };
}

export function formatSpec(spec: BotSpec): string {
  return `${spec.difficulty}:${spec.strategy}`;
}

/**
 * Who plays, from the URL:
 * - `?bot=normal:balanced&side=I` — you against a bot;
 * - `?ivory=hard:hunter&basalt=normal:summit` — two bots.
 * Returns `null` when the URL does not describe a bot game (show the setup form).
 */
export function playersFromParams(params: URLSearchParams): Players | null {
  const bot = parseSpec(params.get('bot'));
  if (bot) {
    const side: Team = params.get('side') === 'B' ? 'B' : 'I';
    return side === 'I'
      ? { I: { kind: 'human' }, B: { kind: 'bot', spec: bot } }
      : { I: { kind: 'bot', spec: bot }, B: { kind: 'human' } };
  }
  const ivory = parseSpec(params.get('ivory'));
  const basalt = parseSpec(params.get('basalt'));
  if (ivory && basalt) {
    return { I: { kind: 'bot', spec: ivory }, B: { kind: 'bot', spec: basalt } };
  }
  return null;
}

export function playersParams(players: Players): Record<string, string> {
  if (players.I.kind === 'bot' && players.B.kind === 'bot') {
    return { ivory: formatSpec(players.I.spec), basalt: formatSpec(players.B.spec) };
  }
  if (players.B.kind === 'bot') return { bot: formatSpec(players.B.spec), side: 'I' };
  if (players.I.kind === 'bot') return { bot: formatSpec(players.I.spec), side: 'B' };
  return {};
}
